import { css } from '@emotion/react'
import SearchIcon from '@mui/icons-material/Search'
import HistoryIcon from '@mui/icons-material/History'

import { Autocomplete } from '../../shared/input'
import { SearchStudent } from '../../../types'
import { DropdownProps } from '../../shared/input/Dropdown'
import type { OnInputChangeEventContext, OnChangeEventContext } from '../../shared/input/Dropdown'
import { baseColor, borderColor, disabledIconColor } from '../../../lib/styles/colors'
import { srOnlyCss } from '../../../lib/styles/cssStyles'
import { StudentProfile } from '../../../generated/graphql'
import { getDisplayName } from '../../../lib/utils'

const adornmentCss = css`
  height: 0.01em;
  display: flex;
  max-height: 2em;
  align-items: center;
  white-space: nowrap;
  color: ${disabledIconColor};
  position: absolute;
  padding-left: 11px;
  padding-bottom: 5px;

  svg {
    padding-top: 4px;
  }
`

const Adornment = ({ children }: { children: React.ReactNode }) => (
  <div css={adornmentCss}>{children}</div>
)

const containerCss = css`
  padding: 6px 16px 0 0;
`

const searchAutocompleteCss = css`
  &.focus:not(.open) .crane-select-input-group,
  &:hover .crane-select-input-group {
    border: 1px solid ${baseColor};
  }

  .crane-select-value-group {
    padding-left: 16px;
    padding-top: 0;
  }

  &.open .crane-select-input-group {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom: none;
  }

  &.open .crane-select-input-group:after {
    position: absolute;
    bottom: 0;
    left: 10px;
    right: 10px;
    border-bottom: 1px solid ${borderColor};
  }

  .crane-select-input-group {
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 10px;
    background-color: #f5f6f9;

    &:focus-within {
      background: white !important;
      border: 1px solid ${baseColor};
    }
  }

  .crane-select-inner-input {
    position: relative;
    padding-left: 28px;
    width: 100%;
  }

  .crane-select-value-group {
    margin-top: 2px;
  }

  .crane-select-menu-container,
  .crane-select-no-options {
    border: 1px solid ${baseColor} !important;
    border-top: none !important;
    border-bottom-left-radius: 4px !important;
    border-bottom-right-radius: 4px !important;
    background: white !important;
    box-shadow: none !important;
  }

  .crane-select-menu-container {
    padding-bottom: 4px;
    width: 500px;
  }
`
const historyStudentCss = css`
  display: flex;
  align-items: center;

  svg {
    display: inline-block;
    color: ${disabledIconColor};
    padding-right: 8px;
  }
`

type StudentSearchAutoCompleteProps = {
  /** Indicates that there are Previous Search results available */
  hasPreviousSearches: boolean
  /** Event callback when user selects a student in results */
  onChange: (e: OnChangeEventContext) => void
  /** Event callback when input in the Search AutoComplete changes */
  onInputChange: (e: OnInputChangeEventContext) => void
  /** Text to search students by for server side searching */
  searchText: string
  /** List of Student Results */
  studentProfiles: Array<StudentProfile>
} & DropdownProps

type OptionRendererParams = {
  option: SearchStudent
}

/** Note: this is duplicated in scheduler monolith. */
export const StudentSearchAutoComplete = ({
  hasPreviousSearches,
  onChange,
  onInputChange,
  searchText,
  studentProfiles,
  ...otherProps
}: StudentSearchAutoCompleteProps) => {
  const handleChange = (e: OnChangeEventContext) => onChange(e)

  const options = studentProfiles.map((s) => ({
    ...s,
    displayName: s ? `${s.firstName} ${s.lastName} - ${s.externalId}` : ''
  }))

  const optionRenderer = ({ option }: OptionRendererParams) => {
    if (!option) {
      return ''
    }

    const studentName = getDisplayName(option)
    const displayName = `${studentName} - ${option.externalId}`
    return option.previouslySearched ? (
      <div css={historyStudentCss}>
        <HistoryIcon /> {displayName}
      </div>
    ) : (
      displayName
    )
  }

  const placeholderText = 'search for a student by name or student ID'

  return (
    <div css={containerCss} className="crane-select-search">
      <Autocomplete
        {...otherProps}
        id="search_students"
        aria-label={placeholderText}
        aria-describedby="search-students-help"
        beforeInput={
          <Adornment>
            <SearchIcon aria-hidden="true" />
          </Adornment>
        }
        css={searchAutocompleteCss}
        inputValue={searchText}
        label="Search Students"
        labelKey="displayName"
        menuAriaLabel="student search results"
        onChange={handleChange}
        onInputChange={onInputChange}
        openOnEmptyInput={hasPreviousSearches}
        optionRenderer={optionRenderer}
        options={options}
        placeholder={placeholderText}
        srOnlyLabel
        valueKey="id"
      />
      <span id="search-students-help" css={srOnlyCss}>
        Type to search for a student by their name or student ID number
      </span>
    </div>
  )
}

export default StudentSearchAutoComplete
